import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import RegisterSignature from "../task/RegisterSignature";
import { PQRSD_STATUS } from "../../constants";

// Officer-side view of the recurso de reposición against a desistimiento
// (CPACA arts. 74–80): how long the citizen has to file it, the recurso once
// filed, and the motivated decision that resolves it. The answers the citizen
// brought with it show in the requerimiento panel above, on the same request.

const MIN_REASON_LEN = 20;

function formatDay(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

function daysUntil(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

const DECISION_CHIP = {
  filed: { label: "Pendiente de decisión", color: "warning" },
  granted: { label: "Se repuso el desistimiento", color: "success" },
  denied: { label: "Se confirmó el desistimiento", color: "error" },
};

export default function DesistimientoAppealPanel({ pqrsd, canResolve = false, busy = false, onResolve }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("");
  const [reason, setReason] = useState("");
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState(null);

  const appeal = pqrsd?.appeal || null;
  // Only a desistimiento decreed with the recurso window has anything to show.
  if (!pqrsd?.appealDeadline && !appeal) return null;
  if (pqrsd.status !== PQRSD_STATUS.DESISTIDO && pqrsd.status !== PQRSD_STATUS.EN_RECURSO && !appeal) return null;

  const windowOpen = !appeal && pqrsd.status === PQRSD_STATUS.DESISTIDO
    && pqrsd.appealDeadline && Date.now() < new Date(pqrsd.appealDeadline).getTime();
  const pending = appeal?.status === "filed" && pqrsd.status === PQRSD_STATUS.EN_RECURSO;
  const chip = appeal ? DECISION_CHIP[appeal.status] : null;
  const decisionLeft = pending ? daysUntil(appeal.decisionDueAt) : null;

  const close = () => {
    setOpen(false);
    setDecision("");
    setReason("");
    setSigned(false);
    setError(null);
  };

  const submit = async () => {
    setError(null);
    try {
      await onResolve(decision, reason.trim());
      close();
    } catch (e) {
      setError(e.message || "No fue posible resolver el recurso.");
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Typography variant="h6">Recurso de reposición</Typography>
        <Typography variant="caption" color="text.secondary">
          Contra el acto que decretó el desistimiento · artículos 74 a 80 de la Ley 1437 de 2011
        </Typography>
        {chip && <Chip size="small" label={chip.label} color={chip.color} />}
      </Stack>

      {windowOpen && (
        <Alert severity="info">
          El acto se notificó por correo el <strong>{formatDay(pqrsd.desistimientoNotifiedAt)}</strong>. El
          ciudadano puede interponer el recurso hasta el <strong>{formatDay(pqrsd.appealLastDay)}</strong> (diez
          días hábiles), aportando la información requerida.
          {pqrsd.appealReminderSentAt
            ? ` Se le recordó el ${formatDay(pqrsd.appealReminderSentAt)}.`
            : ` Se le enviará un recordatorio el ${formatDay(pqrsd.appealReminderAt)}.`}
        </Alert>
      )}

      {!appeal && !windowOpen && (
        <Alert severity="error">
          El plazo para el recurso venció el <strong>{formatDay(pqrsd.appealLastDay)}</strong> sin que se
          interpusiera. El desistimiento quedó en firme
          {pqrsd.desistimientoFinalAt ? ` (${formatDay(pqrsd.desistimientoFinalAt)})` : ""} y se le informó al
          ciudadano.
        </Alert>
      )}

      {appeal && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Interpuesto el {formatDay(appeal.filedAt)}
            {pending && appeal.decisionDueAt && (
              <> · resolver a más tardar el <strong>{formatDay(appeal.decisionDueAt)}</strong>
                {decisionLeft != null && decisionLeft >= 0 ? ` (${decisionLeft} días)` : ""}</>
            )}
          </Typography>
          <Typography variant="subtitle2">Motivos del recurso</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 1.5, pl: 1, borderLeft: 3, borderColor: "divider" }}>
            {appeal.grounds || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            La información que aportó el ciudadano aparece en el requerimiento de arriba.
          </Typography>

          {appeal.status !== "filed" && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Decisión</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDay(appeal.decidedAt)}{appeal.decidedByName ? ` · ${appeal.decidedByName}` : ""}
              </Typography>
              {appeal.reason && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>{appeal.reason}</Typography>
              )}
              {appeal.decisionFiledId && (
                <Link
                  href={appeal.decisionFileUrl || undefined}
                  target="_blank"
                  rel="noopener"
                  variant="body2"
                  sx={{ fontFamily: "monospace", display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
                >
                  <DescriptionIcon fontSize="small" /> {appeal.decisionFiledId}
                </Link>
              )}
            </Box>
          )}

          {pending && canResolve && (
            <Button
              variant="contained"
              startIcon={<GavelIcon />}
              sx={{ mt: 2 }}
              disabled={busy}
              onClick={() => setOpen(true)}
            >
              Resolver recurso
            </Button>
          )}
        </Box>
      )}

      <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
        <DialogTitle>Resolver el recurso de reposición</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se radicará un acto administrativo motivado que resuelve el recurso y se notificará al ciudadano
            por correo. Contra esta decisión no procede recurso alguno.
          </Typography>
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Decisión</FormLabel>
            <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value)}>
              <FormControlLabel
                value="grant"
                control={<Radio />}
                label="Reponer: revocar el desistimiento y continuar el trámite con la información aportada"
              />
              <FormControlLabel
                value="deny"
                control={<Radio />}
                label="Confirmar: mantener el desistimiento y el archivo"
              />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Motivación de la decisión"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={`Mínimo ${MIN_REASON_LEN} caracteres.`}
            sx={{ mb: 2 }}
          />
          <RegisterSignature actionName="resuelto y firmado" edit={!busy} signed={signed} setSigned={setSigned} />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancelar</Button>
          <Button
            variant="contained"
            color={decision === "deny" ? "error" : "primary"}
            disabled={busy || !decision || reason.trim().length < MIN_REASON_LEN || !signed}
            onClick={submit}
          >
            {decision === "deny" ? "Confirmar el desistimiento" : "Reponer y continuar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
